import React from "react";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import { Clock } from "lucide-react";

import {
  AddTimezoneAction,
  GetAllTimezonesAction,
  UpdateTimezoneAction,
} from "store/middlewares/dataManagement/timezone";
import { Input, Label, Button } from "components/ui";
import { SubmitLoaderButton } from "components/atoms";
import { closeAdminModal } from "store/slices/common/adminModal.slice";

function ModalEditTimezone({ row, filter }) {
  const dispatch = useDispatch();

  const handleSubmit = (values) => {
    const payload = {
      title: values.title.trim(),
      value: values.value,
      gmtFormat: values.gmtFormat.trim(),
      offsetMinutes: Number(values.offsetMinutes),
    };

    if (row) {
      dispatch(
        UpdateTimezoneAction({
          ...payload,
          id: row.id,
        }),
      );
    } else {
      dispatch(
        AddTimezoneAction({
          ...payload,
          filter,
          onSuccess: () => {
            dispatch(GetAllTimezonesAction(filter));
          },
        }),
      );
    }
  };

  const formik = useFormik({
    initialValues: {
      title: row?.title ?? "",
      value: row?.value ?? "",
      offsetMinutes: row?.offset_minutes ?? "",
      gmtFormat: row?.gmt_format ?? "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      value: Yup.string().required("Value is required"),
      offsetMinutes: Yup.string().required("Offset ( Minutes ) is required"),
      gmtFormat: Yup.string().required("GMT format is required"),
    }),
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  return (
    <form onSubmit={formik.handleSubmit} className="grid gap-5">
      {/* Title with icon */}
      <div className="flex items-center gap-3.5">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700">
          <Clock className="size-5" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-bold text-foreground">
          {row ? "Edit timezone" : "Add timezone"}
        </h2>
      </div>

      {/* Inputs (Stacked label + input) */}
      <div className="grid gap-4">
        {/* Title */}
        <div className="flex flex-col gap-1.5">
          <Label>Title</Label>
          <Input
            name="title"
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.title && formik.touched.title}
            errorMsg={formik.errors.title}
            placeholder="Enter Title"
            className="w-full"
            variant="lg"
          />
        </div>

        {/* Timezone Value */}
        <div className="flex flex-col gap-1.5">
          <Label>Timezone</Label>
          <Input
            name="value"
            value={formik.values.value}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={row ? "" : "Enter timezone (e.g. Asia/Kolkata)"}
            disabled={!!row} // Keep disabled but remove the gray bg class
            className="w-full"
            variant="lg"
          />
        </div>

        {/* GMT Format */}
        <div className="flex flex-col gap-1.5">
          <Label>GMT Format</Label>
          <Input
            name="gmtFormat"
            value={formik.values.gmtFormat}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.gmtFormat && formik.touched.gmtFormat}
            errorMsg={formik.errors.gmtFormat}
            placeholder="Enter GMT format"
            className="w-full"
            variant="lg"
          />
        </div>

        {/* Offset Minutes */}
        <div className="flex flex-col gap-1.5">
          <Label>Offset ( Minutes )</Label>
          <Input
            name="offsetMinutes"
            value={formik.values.offsetMinutes}
            isMinusNumeric
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.offsetMinutes && formik.touched.offsetMinutes}
            errorMsg={formik.errors.offsetMinutes}
            placeholder="Enter offset ( minutes )"
            className="w-full"
            variant="lg"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => dispatch(closeAdminModal())}
        >
          Cancel
        </Button>
        <SubmitLoaderButton
          btnText={row ? "Update" : "Add"}
          isDisable={formik.isSubmitting}
        />
      </div>
    </form>
  );
}

export default ModalEditTimezone;